import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Icons } from "@/components/ui";
import { useTasksStore } from "@/stores/tasks-store";
import type { Task } from "@/api/tasks";
import { formatDate, useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { CalendarDayCell } from "./calendar-day-cell";
import { CalendarDayPocket } from "./calendar-day-pocket";
import { calendarMonthDays, localDateKey, taskDueDate } from "./task-dates";
import { useCalendarOpening } from "./use-calendar-opening";
import { useLocalCalendarDay } from "./use-local-calendar-day";
import {
  filterTasksForView,
  useTaskDisplayState,
} from "./task-display-filters";
/** Monday 1 January 2024 to Sunday 7 January 2024: the grid's weekday order. */
const WEEK_DAYS = Array.from(
  { length: 7 },
  (_, index) => new Date(2024, 0, 1 + index),
);

export function CalendarView({
  tasks,
  onSelectTask,
}: {
  tasks: Task[];
  onSelectTask: (taskId: string) => void;
}) {
  const t = useT(framesTaskboardMessages);
  const month = useTasksStore((state) => state.calendarMonth);
  const setMonth = useTasksStore((state) => state.setCalendarMonth);
  const display = useTaskDisplayState();
  const today = useLocalCalendarDay();
  const [openDay, setOpenDay] = useState<{
    date: Date;
    monthKey: string;
  } | null>(null);
  const monthKey = localDateKey(month).slice(0, 7);
  const closeDay = useCallback(() => setOpenDay(null), []);
  const dates = useMemo(() => calendarMonthDays(month), [month]);
  const visibleTasks = useMemo(
    () => filterTasksForView(tasks, display, "calendar"),
    [tasks, display],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  useCalendarOpening(scrollRef, visibleTasks, today);
  const tasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of visibleTasks) {
      const date = taskDueDate(task.due_at);
      if (!date) continue;
      const key = localDateKey(date);
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    }
    return map;
  }, [visibleTasks]);
  if (
    openDay &&
    (openDay.monthKey !== monthKey ||
      !tasksByDate.get(localDateKey(openDay.date))?.length)
  ) {
    setOpenDay(null);
  }
  const changeMonth = (date: Date) => {
    setOpenDay(null);
    setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };
  return (
    <div className="relative flex h-full flex-col" data-testid="calendar-view">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-appkit-body font-semibold text-primary">
          {formatDate(month, { month: "long", year: "numeric" })}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={t("previousMonth")}
            data-testid="calendar-prev"
            onClick={() =>
              changeMonth(
                new Date(month.getFullYear(), month.getMonth() - 1, 1),
              )
            }
          >
            <Icons.ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="calendar-today"
            onClick={() => changeMonth(new Date())}
          >
            {t("today")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            aria-label={t("nextMonth")}
            data-testid="calendar-next"
            onClick={() =>
              changeMonth(
                new Date(month.getFullYear(), month.getMonth() + 1, 1),
              )
            }
          >
            <Icons.ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-1">
        {WEEK_DAYS.map((day) => (
          <span
            key={day.getDay()}
            className="truncate text-center text-appkit-small text-muted"
          >
            {formatDate(day, { weekday: "short" })}
          </span>
        ))}
      </div>
      <div
        ref={scrollRef}
        className="scrollbar-none relative min-h-0 flex-1 overflow-auto"
        data-testid="calendar-scroll"
      >
        <div
          className="grid grid-cols-7 gap-1"
          data-testid={`calendar-month-${localDateKey(month).slice(0, 7)}`}
        >
          {dates.map((date) => (
            <CalendarDayCell
              key={localDateKey(date)}
              date={date}
              tasks={tasksByDate.get(localDateKey(date)) ?? []}
              isToday={localDateKey(date) === localDateKey(today)}
              isCurrentMonth={date.getMonth() === month.getMonth()}
              onSelectTask={onSelectTask}
              onShowMore={() => setOpenDay({ date, monthKey })}
            />
          ))}
        </div>
      </div>
      {openDay && openDay.monthKey === monthKey && (
        <CalendarDayPocket
          date={openDay.date}
          tasks={tasksByDate.get(localDateKey(openDay.date)) ?? []}
          onSelectTask={(taskId) => {
            closeDay();
            onSelectTask(taskId);
          }}
          onClose={closeDay}
        />
      )}
    </div>
  );
}
